import React, { useState, useEffect, useRef } from "react";
import { VList } from "../VList";
import { useNavigate } from "react-router-dom";
import { Search, Users2, BookOpen, GraduationCap } from "lucide-react";
import { api } from "../../lib/api";
import { Avatar } from "../Avatar";
import { RankBadge } from "../RankBadge";
import { AlphaDenBadge } from "../rooms/AlphaDenBadge";

export function GlobalSearch() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scope, setScope] = useState("all");
  const rootRef = useRef(null);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 1) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}&scope=${scope}`);
        setResults(r.data);
        setSearchOpen(true);
      } catch (e) { console.debug("search failed", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, scope]);

  // Close the results panel when clicking anywhere outside the search area.
  useEffect(() => {
    if (!searchOpen) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [searchOpen]);

  const goTo = (path) => {
    setSearch(""); setResults(null); setSearchOpen(false);
    navigate(path);
  };

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/mates?q=${encodeURIComponent(search.trim())}`);
    setSearchOpen(false);
  };

  return (
    <form ref={rootRef} onSubmit={doSearch} className="flex-1 max-w-md mx-auto hidden md:block relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          data-testid="global-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => results && setSearchOpen(true)}
          placeholder="Search wolves, dens, howls..."
          className="w-full bg-slate-900/70 border border-slate-800 rounded-full pl-10 pr-4 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50 transition-colors"
        />
      </div>
      {searchOpen && results && (
        <div className="absolute top-full mt-2 inset-x-0 bg-slate-950/95 backdrop-blur-xl border border-slate-800 rounded-xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto animate-slide-up z-50" data-testid="search-results">
          <ScopeChips scope={scope} onChange={setScope} />
          {["wolves", "dens", "howls", "blog", "academy"].every((k) => !(results[k] || []).length) && (
            <div className="px-4 py-6 text-center text-slate-500 text-sm">No tracks found in the snow.</div>
          )}
          {(results.wolves || []).length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500">Wolves</div>
              {results.wolves.map((w) => (
                <button key={w.id} data-testid="search-result-wolf" onMouseDown={() => goTo(`/w/${w.username}`)} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-800/60 text-left">
                  <Avatar user={w} size="xs" showPresence showVerified />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate flex items-center gap-1.5">{w.name} <RankBadge rank={w.rank} showIcon={false} className="scale-[0.7]" /></div>
                    <div className="text-xs text-slate-500 truncate">@{w.username}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {(results.dens || []).length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500">Dens</div>
              {results.dens.map((d) => (
                <button key={d.id} data-testid="search-result-den" onMouseDown={() => goTo("/dens")} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-800/60 text-left">
                  <span className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center"><Users2 className="w-4 h-4 text-sky-400" /></span>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate flex items-center gap-1.5"><span className="truncate">{d.name}</span>{d.is_alpha_den && <AlphaDenBadge />}</div>
                    <div className="text-xs text-slate-500 truncate">{d.topic}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {(results.howls || []).length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500">Howls</div>
              <VList items={results.howls} maxHeight={260} rowHeight={52} testid="search-howls-virtual-list" render={(h) => (
                <button data-testid="search-result-howl" onMouseDown={() => goTo(`/w/${h.author.username}`)} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-800/60 text-left">
                  <Avatar user={h.author} size="xs" />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-300 truncate">{h.content}</div>
                    <div className="text-xs text-slate-500 truncate">by {h.author.name}</div>
                  </div>
                </button>
              )} />
            </div>
          )}
          <ExtraResults results={results} goTo={goTo} />
        </div>
      )}
    </form>
  );
}

const SCOPES = [["all", "All"], ["wolves", "Wolves"], ["dens", "Dens"], ["howls", "Howls"], ["blog", "Blog"], ["academy", "University"]];

function ScopeChips({ scope, onChange }) {
  return (
    <div className="flex gap-1.5 px-3 pt-3 pb-1 overflow-x-auto" data-testid="search-scope-chips">
      {SCOPES.map(([id, label]) => (
        <button key={id} type="button" onMouseDown={(e) => { e.preventDefault(); onChange(id); }} data-testid={`search-scope-${id}`}
          className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${scope === id ? "bg-sky-500 text-slate-950" : "bg-slate-800/70 text-slate-400 hover:text-slate-200"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function ExtraResults({ results, goTo }) {
  const sections = [
    ["blog", "Blog", BookOpen, (b) => `/blog/${b.id}`, (b) => b.title, () => "Pack Chronicles"],
    ["academy", "Pack University", GraduationCap, (d) => `/academy/category/${encodeURIComponent(d.category || "")}`, (d) => d.title, (d) => d.category],
  ];
  return sections.map(([key, label, Icon, href, title, sub]) => (results[key] || []).length > 0 && (
    <div key={key}>
      <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      {results[key].map((r) => (
        <button key={r.id} type="button" data-testid={`search-result-${key}`} onMouseDown={() => goTo(href(r))} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-800/60 text-left">
          <span className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center"><Icon className="w-4 h-4 text-amber-400" /></span>
          <div className="min-w-0">
            <div className="text-sm text-slate-200 truncate">{title(r)}</div>
            <div className="text-xs text-slate-500 truncate">{sub(r)}</div>
          </div>
        </button>
      ))}
    </div>
  ));
}
